import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  apiClient,
  getApiBase,
  registerAuthSessionHandlers,
  applyAccessTokenToStorage,
  resetSessionExpiredFlag,
} from '../lib/api';

const AuthContext = createContext(null);

const API = getApiBase();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const applyAccessToken = useCallback((newToken) => {
    const t = applyAccessTokenToStorage(newToken);
    setToken(t || null);
    return t;
  }, []);

  useEffect(() => {
    registerAuthSessionHandlers({
      onTokenRefreshed: (newToken) => {
        applyAccessToken(newToken);
      },
      onSessionExpired: () => {
        setUser(null);
        setToken(null);
      },
    });
  }, [applyAccessToken]);

  const checkAuth = useCallback(async () => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const response = await apiClient.get(`${API}/auth/me`);
      setUser(response.data);
    } catch {
      setUser(null);
      setToken(null);
      applyAccessTokenToStorage(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (loginField, password) => {
    resetSessionExpiredFlag();
    const response = await apiClient.post(`${API}/auth/login`, {
      login: loginField,
      password,
    });
    const { token: newToken, ...userData } = response.data;
    if (newToken) {
      applyAccessToken(newToken);
    }
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await apiClient.post(`${API}/auth/logout`, {});
    } catch {
      // Ignore errors
    }
    setUser(null);
    setToken(null);
    applyAccessTokenToStorage(null);
  };

  const getAuthHeaders = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, logout, checkAuth, getAuthHeaders, token, applyAccessToken }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
